import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { io, Socket } from 'socket.io-client';
import api from '../services/api';

// Extrae la URL base sin /api/v1 para el WebSocket
const WS_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api\/v\d+\/?$/, '');

const makeIcon = (color: string, pulse = false) => L.divIcon({
  className: '',
  html: `<div style="width:16px;height:16px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 3px ${color}44${pulse ? ';animation:pulse 1s infinite' : ''}"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const iconActivo   = makeIcon('#22C55E');
const iconInactivo = makeIcon('#6B7280');
const iconSinSenal = makeIcon('#F59E0B');
const iconSos      = makeIcon('#EF4444', true);

const MapaCobradores: React.FC = () => {
  const [cobradores, setCobradores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [seleccionado, setSeleccionado] = useState<any>(null);
  const [ultimaActualizacion, setUltimaActualizacion] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);
  const [wsConectado, setWsConectado] = useState(false);
  const intervalRef = useRef<any>(null);
  const socketRef = useRef<Socket | null>(null);
  const cobradoresRef = useRef<any[]>([]);
  const [historial, setHistorial] = useState<any[]>([]);
  const [cobradorHistorialId, setCobradorHistorialId] = useState<string | null>(null);
  const [fechaHistorial, setFechaHistorial] = useState(() => new Date().toISOString().slice(0, 10));
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const actualizarHora = () => {
    const ahora = new Date();
    setUltimaActualizacion(
      `${ahora.getHours().toString().padStart(2,'0')}:${ahora.getMinutes().toString().padStart(2,'0')}:${ahora.getSeconds().toString().padStart(2,'0')}`
    );
  };

  const cargar = async () => {
    try {
      const res = await api.get('/tracking/cobradores');
      setCobradores(res.data);
      cobradoresRef.current = res.data;
      actualizarHora();
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    cargar();
    // Fallback polling cada 30s (el WebSocket es el canal principal)
    intervalRef.current = setInterval(cargar, 30000);

    // Conexión WebSocket para actualizaciones en tiempo real
    const socket = io(WS_URL + '/tracking', {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('connect', () => setWsConectado(true));
    socket.on('disconnect', () => setWsConectado(false));

    socket.on('posicion', (data: any) => {
      // Actualiza solo el cobrador que mandó la posición, sin recargar todo
      setCobradores(prev => {
        const actualizado = prev.map(c => {
          if (c.cobrador.id !== data.cobradorId) return c;
          return {
            ...c,
            posicion: {
              latitud: data.latitud,
              longitud: data.longitud,
              velocidad: data.velocidad,
              timestamp: data.timestamp,
              esSos: data.esSos,
            },
            minutosDesdeUltimo: 0,
            activo: true,
            sinSenial: false,
          };
        });
        cobradoresRef.current = actualizado;
        return actualizado;
      });
      actualizarHora();
    });

    return () => {
      clearInterval(intervalRef.current);
      socket.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verHistorial = async (cobradorId: string) => {
    setCargandoHistorial(true);
    setCobradorHistorialId(cobradorId);
    try {
      const res = await api.get(`/tracking/historial/${cobradorId}?fecha=${fechaHistorial}`);
      setHistorial(res.data || []);
    } catch { setHistorial([]); }
    finally { setCargandoHistorial(false); }
  };

  const limpiarHistorial = () => { setHistorial([]); setCobradorHistorialId(null); };

  const toggleGps = async (cobradorId: string, actual: boolean) => {
    setToggling(cobradorId);
    try {
      await api.put(`/usuarios/${cobradorId}/gps`, { gpsHabilitado: !actual });
      await cargar();
    } catch { alert('Error al cambiar GPS'); }
    finally { setToggling(null); }
  };

  const activos    = cobradores.filter(c => c.activo).length;
  const sinSenial  = cobradores.filter(c => c.sinSenial).length;
  const alertasSos = cobradores.filter(c => c.alertaSos).length;
  const conPosicion = cobradores.filter(c => c.posicion);

  const centroMapa: [number, number] = conPosicion.length > 0
    ? [
        conPosicion.reduce((s: number, c: any) => s + Number(c.posicion.latitud), 0) / conPosicion.length,
        conPosicion.reduce((s: number, c: any) => s + Number(c.posicion.longitud), 0) / conPosicion.length,
      ]
    : [-12.046374, -77.042793];

  const getIcon = (c: any) => {
    if (c.alertaSos) return iconSos;
    if (c.sinSenial) return iconSinSenal;
    if (c.activo)    return iconActivo;
    return iconInactivo;
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', fontWeight:700, fontSize:20, margin:0 }}>Mapa de Cobradores</h2>
          <span style={{ color:'#64748b', fontSize:13 }}>Ubicacion en tiempo real via WebSocket</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {alertasSos > 0 && (
            <div style={{ background:'#ef444422', border:'1px solid #ef4444', borderRadius:8, padding:'6px 14px', fontSize:13, color:'#ef4444', fontWeight:700, animation:'pulse 1s infinite' }}>
              🆘 {alertasSos} SOS ACTIVO{alertasSos > 1 ? 'S' : ''}
            </div>
          )}
          {sinSenial > 0 && (
            <div style={{ background:'#f59e0b22', border:'1px solid #f59e0b44', borderRadius:8, padding:'6px 14px', fontSize:13, color:'#f59e0b', fontWeight:600 }}>
              ⚠️ {sinSenial} sin señal
            </div>
          )}
          <div style={{ background:'#22C55E22', border:'1px solid #22C55E44', borderRadius:8, padding:'6px 14px', fontSize:13, color:'#22C55E', fontWeight:600 }}>
            {activos} activo{activos !== 1 ? 's' : ''}
          </div>
          <button onClick={cargar} style={{ background:'#0ea5e922', border:'1px solid #0ea5e944', borderRadius:8, padding:'6px 12px', color:'#38bdf8', cursor:'pointer', fontSize:13 }}>
            ↻ Actualizar
          </button>
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color: wsConectado ? '#22C55E' : '#f59e0b' }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background: wsConectado ? '#22C55E' : '#f59e0b', display:'inline-block' }} />
            {wsConectado ? 'En vivo' : 'Reconectando...'}
          </span>
          {ultimaActualizacion && <span style={{ color:'#475569', fontSize:12 }}>Última: {ultimaActualizacion}</span>}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:16, height:'calc(100vh - 160px)' }}>

        {/* Lista */}
        <div style={{ background:'#0f172a', border:'1px solid #1e293b', borderRadius:12, overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid #1e293b', color:'#94a3b8', fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>
            Cobradores ({cobradores.length})
          </div>
          <div style={{ flex:1, overflowY:'auto' }}>
            {loading ? (
              <div style={{ padding:24, textAlign:'center', color:'#475569' }}>Cargando...</div>
            ) : cobradores.map(c => {
              const gpsOn = c.cobrador.gpsHabilitado;
              const nombre = `${c.cobrador.nombre} ${c.cobrador.apellido}`;
              const min = c.minutosDesdeUltimo;
              const isSel = seleccionado?.cobrador?.id === c.cobrador.id;
              const dotColor = c.alertaSos ? '#ef4444' : c.sinSenial ? '#f59e0b' : c.activo ? '#22C55E' : '#6b7280';

              return (
                <div key={c.cobrador.id}
                  onClick={() => setSeleccionado(isSel ? null : c)}
                  style={{
                    padding:'12px 16px', cursor:'pointer', borderBottom:'1px solid #1e293b',
                    background: c.alertaSos ? '#ef444411' : c.sinSenial ? '#f59e0b11' : isSel ? '#0ea5e911' : 'transparent',
                    borderLeft: `3px solid ${isSel ? '#38bdf8' : c.alertaSos ? '#ef4444' : c.sinSenial ? '#f59e0b' : 'transparent'}`,
                  }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {/* Avatar */}
                    <div style={{ position:'relative', flexShrink:0 }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:`${dotColor}22`, border:`1px solid ${dotColor}`, display:'flex', alignItems:'center', justifyContent:'center', color:dotColor, fontWeight:700, fontSize:13 }}>
                        {nombre.split(' ').map((p:string) => p[0]).join('').slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ position:'absolute', bottom:0, right:0, width:10, height:10, borderRadius:'50%', background:dotColor, border:'2px solid #0f172a' }} />
                    </div>

                    {/* Info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.alertaSos && '🆘 '}{nombre}
                      </div>
                      <div style={{ fontSize:11, color: c.alertaSos ? '#ef4444' : c.sinSenial ? '#f59e0b' : c.activo ? '#22C55E' : '#6b7280', marginTop:1 }}>
                        {c.alertaSos ? `⚠️ SOS hace ${min} min` :
                         c.sinSenial ? `Sin señal — último: hace ${min ?? '?'} min` :
                         c.posicion ? (c.activo ? `Activo — hace ${min} min` : `Inactivo — hace ${min} min`) :
                         'Sin GPS hoy'}
                      </div>
                    </div>

                    {/* Toggle GPS */}
                    <div onClick={e => { e.stopPropagation(); toggleGps(c.cobrador.id, gpsOn); }}
                      style={{ flexShrink:0, width:44, height:24, borderRadius:12, background: gpsOn ? '#22c55e' : '#334155',
                        cursor: toggling === c.cobrador.id ? 'wait' : 'pointer', position:'relative', transition:'background .2s' }}>
                      <div style={{ position:'absolute', top:2, left: gpsOn ? 22 : 2, width:20, height:20,
                        borderRadius:'50%', background:'white', transition:'left .2s', boxShadow:'0 1px 3px #0004' }} />
                    </div>
                  </div>

                  {isSel && (
                    <div style={{ marginTop:10 }}>
                      {c.posicion && (
                        <div style={{ background:'#1e293b', borderRadius:8, padding:'8px 10px', fontSize:11, color:'#94a3b8', marginBottom:8 }}>
                          <div>Lat: {Number(c.posicion.latitud).toFixed(6)}</div>
                          <div>Lng: {Number(c.posicion.longitud).toFixed(6)}</div>
                          <div style={{ marginTop:4, color:'#475569' }}>{new Date(c.posicion.timestamp).toLocaleString('es-PE')}</div>
                        </div>
                      )}
                      {/* Controles de historial */}
                      <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                        <input type="date" value={fechaHistorial} onChange={e => setFechaHistorial(e.target.value)}
                          style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:6, color:'#94a3b8', padding:'3px 6px', fontSize:11, flex:1, minWidth:110 }} />
                        <button onClick={() => verHistorial(c.cobrador.id)}
                          style={{ background:'#22c55e22', border:'1px solid #22c55e44', color:'#22c55e', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:11, whiteSpace:'nowrap' }}>
                          {cargandoHistorial && cobradorHistorialId === c.cobrador.id ? '...' : '📍 Recorrido'}
                        </button>
                        {cobradorHistorialId === c.cobrador.id && historial.length > 0 && (
                          <button onClick={limpiarHistorial}
                            style={{ background:'#ef444422', border:'1px solid #ef444444', color:'#ef4444', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:11 }}>
                            ✕
                          </button>
                        )}
                      </div>
                      {cobradorHistorialId === c.cobrador.id && historial.length > 0 && (
                        <div style={{ marginTop:6, fontSize:11, color:'#64748b' }}>
                          {historial.length} puntos · {new Date(historial[0].timestamp).toLocaleTimeString('es-PE')} → {new Date(historial[historial.length-1].timestamp).toLocaleTimeString('es-PE')}
                        </div>
                      )}
                      {cobradorHistorialId === c.cobrador.id && !cargandoHistorial && historial.length === 0 && (
                        <div style={{ marginTop:6, fontSize:11, color:'#475569' }}>Sin recorrido ese día</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mapa */}
        <div style={{ borderRadius:12, overflow:'hidden', border:'1px solid #1e293b' }}>
          {!loading && (
            <MapContainer center={centroMapa} zoom={conPosicion.length > 0 ? 14 : 12} style={{ height:'100%', width:'100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
              {historial.length > 1 && (
                <Polyline
                  positions={historial.map((p: any) => [Number(p.latitud), Number(p.longitud)] as [number, number])}
                  color="#22C55E" weight={3} opacity={0.8}
                />
              )}
              {cobradores.filter(c => c.posicion).map(c => (
                <Marker key={c.cobrador.id}
                  position={[Number(c.posicion.latitud), Number(c.posicion.longitud)]}
                  icon={getIcon(c)}>
                  <Popup>
                    <div style={{ minWidth:170 }}>
                      <strong>{c.cobrador.nombre} {c.cobrador.apellido}</strong>
                      {c.alertaSos && <div style={{ color:'#ef4444', fontWeight:700, fontSize:13 }}>🆘 ALERTA SOS</div>}
                      {c.sinSenial && <div style={{ color:'#f59e0b', fontSize:12 }}>⚠️ Sin señal hace {c.minutosDesdeUltimo} min</div>}
                      <div style={{ color: c.activo ? '#16a34a' : '#6b7280', fontSize:12, marginTop:2 }}>
                        {c.activo ? '● Activo' : '● Inactivo'} — hace {c.minutosDesdeUltimo} min
                      </div>
                      <div style={{ fontSize:12, marginTop:4 }}>
                        GPS: <span style={{ color: c.cobrador.gpsHabilitado ? '#22c55e' : '#ef4444' }}>
                          {c.cobrador.gpsHabilitado ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                      </div>
                      <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{new Date(c.posicion.timestamp).toLocaleString('es-PE')}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
          {loading && <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#0f172a', color:'#475569' }}>Cargando mapa...</div>}
        </div>
      </div>
    </div>
  );
};

export default MapaCobradores;
